import { LocaleLink } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import {
  ArrowRight,
  Target,
  TrendingUp,
  Shield,
  Users,
  Zap,
  BadgeCheck,
  Clock,
  HandCoins,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import Brand from "@/components/ui/brand";
import WhatIsCraftfindus from "@/components/seo/WhatIsCraftfindus";
import heroBg from "@/assets/hero-craft-bg.jpg";
import { useTranslation } from "react-i18next";

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  const id = q.replace(/\s+/g, "-").toLowerCase().slice(0, 30);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group min-h-[44px]"
        aria-expanded={open}
        aria-controls={`faq-${id}`}
      >
        <span className="text-body font-medium pr-4">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <p id={`faq-${id}`} className="text-caption leading-relaxed pb-5 pr-8">{a}</p>
      )}
    </div>
  );
};

const FuerHandwerker = () => {
  const { t } = useTranslation();

  const benefits = [
    { icon: Target, title: t("handwerker.benefit1Title"), description: t("handwerker.benefit1Desc") },
    { icon: Users, title: t("handwerker.benefit2Title"), description: t("handwerker.benefit2Desc") },
    { icon: HandCoins, title: t("handwerker.benefit3Title"), description: t("handwerker.benefit3Desc") },
    { icon: Shield, title: t("handwerker.benefit4Title"), description: t("handwerker.benefit4Desc") },
    { icon: TrendingUp, title: t("handwerker.benefit5Title"), description: t("handwerker.benefit5Desc") },
    { icon: Zap, title: t("handwerker.benefit6Title"), description: t("handwerker.benefit6Desc") },
  ];

  const steps = [
    { step: "01", title: t("handwerker.step1Title"), description: t("handwerker.step1Desc") },
    { step: "02", title: t("handwerker.step2Title"), description: t("handwerker.step2Desc") },
    { step: "03", title: t("handwerker.step3Title"), description: t("handwerker.step3Desc") },
  ];

  const faqs = [
    { q: t("handwerker.faq1q", { defaultValue: "Was kostet CRAFTFINDUS für Handwerksbetriebe?" }), a: t("handwerker.faq1a", { defaultValue: "Sie zahlen nur pro angenommener Projektanfrage einen transparenten Fixpreis. Keine monatlichen Gebühren, keine Provisionen. Sie entscheiden selbst, welche Projekte Sie annehmen." }) },
    { q: t("handwerker.faq2q", { defaultValue: "Wie funktioniert das Matching?" }), a: t("handwerker.faq2a", { defaultValue: "Unser Algorithmus analysiert jede Anfrage und gleicht sie mit Ihrem Profil ab: Gewerk, Spezialisierung, Region und Kapazität. Nur bei hoher Passung erhalten Sie die Anfrage." }) },
    { q: t("handwerker.faq3q", { defaultValue: "Wie viele Anfragen bekomme ich?" }), a: t("handwerker.faq3a", { defaultValue: "Das hängt von Ihrem Gewerk, Ihrer Region und Ihrem Radius ab. Sie können ein Monatslimit setzen. Qualität geht bei uns immer vor Quantität." }) },
    { q: t("handwerker.faq4q", { defaultValue: "Gibt es eine Vertragsbindung?" }), a: t("handwerker.faq4a", { defaultValue: "Nein. Sie können CRAFTFINDUS jederzeit ohne Kündigungsfrist verlassen. Keine Mindestlaufzeit, keine versteckten Kosten." }) },
    { q: t("handwerker.faq5q", { defaultValue: "Was unterscheidet CRAFTFINDUS von anderen Portalen?" }), a: t("handwerker.faq5a", { defaultValue: "Kein Bieten, kein Preisdumping. Bei CRAFTFINDUS werden Handwerksbetriebe ausgewählt, nicht gesucht. Die Limitierung auf 3 bis 5 Betriebe pro Anfrage sichert faire Chancen." }) },
  ];

  return (
    <div className="min-h-screen bg-background" id="main-content">
      <SEOHead
        title={t("handwerker.seoTitle")}
        description={t("handwerker.seoDesc")}
        canonical="/fuer-handwerker"
      />
      <JsonLd
        includeService
        includeFaq
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
          { name: t("footer.forCraftBusinesses"), url: "https://www.craftfindus.com/fuer-handwerker" },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden" aria-label={t("handwerker.heroLabel")}>
        <div className="absolute inset-0">
          <img src={heroBg} alt={t("handwerker.seoDesc")} className="w-full h-full object-cover" loading="eager" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 section-padding pb-20 md:pb-24 pt-24 md:pt-32 w-full">
          <div className="max-w-4xl">
            <span className="text-micro tracking-[0.15em] md:tracking-[0.2em] opacity-0 animate-fade-up">
              {t("handwerker.heroLabel")}
            </span>
            <h1 className="text-display mt-4 md:mt-6 opacity-0 animate-fade-up delay-100">
              {t("handwerker.heroTitle1")}<br />
              <span className="text-accent">{t("handwerker.heroTitle2")}</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mt-6 md:mt-8 leading-relaxed opacity-0 animate-fade-up delay-200">
              {t("handwerker.heroDesc", { brand: "CRAFTFINDUS" })}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 md:mt-10 opacity-0 animate-fade-up delay-300">
              <LocaleLink to="/registrieren">
                <Button variant="commit" size="lg" className="group w-full sm:w-auto">
                  {t("handwerker.registerFree")}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </LocaleLink>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-8 opacity-0 animate-fade-up delay-400">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <BadgeCheck className="w-4 h-4 text-accent" /> {t("handwerker.badgeNoContract")}
              </span>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4 text-accent" /> {t("handwerker.badgeReady")}
              </span>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-accent" /> {t("handwerker.badgeVerified")}
              </span>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <HandCoins className="w-4 h-4 text-accent" /> {t("handwerker.badgePayOnAccept")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <span className="text-micro tracking-[0.15em] md:tracking-[0.2em]">{t("handwerker.benefitsLabel")}</span>
          <h2 className="text-headline mt-4 mb-8 md:mb-12">
            {t("handwerker.benefitsTitle", { brand: "CRAFTFINDUS" })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {benefits.map((b) => (
              <div key={b.title} className="opportunity-card space-y-3 md:space-y-4 !p-5 md:!p-8">
                <b.icon className="w-6 h-6 text-accent" aria-hidden="true" />
                <h3 className="text-subheadline">{b.title}</h3>
                <p className="text-caption leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="section-padding py-16 md:py-20 border-t border-border">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <span className="text-micro tracking-[0.15em] md:tracking-[0.2em]">{t("handwerker.pricingLabel")}</span>
          <h2 className="text-headline mt-4">{t("handwerker.pricingTitle")}</h2>
          <p className="text-caption max-w-2xl mx-auto leading-relaxed">{t("handwerker.pricingDesc")}</p>
          <LocaleLink to="/craft/pricing">
            <Button variant="commit" size="lg" className="group mt-4">
              {t("pricing.landingCta")}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </LocaleLink>
        </div>
      </section>

      {/* How it works */}
      <section className="section-padding py-20 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <span className="text-micro tracking-[0.2em]">{t("handwerker.howLabel")}</span>
          <h2 className="text-headline mt-4 mb-12">{t("handwerker.howTitle")}</h2>
          <div className="space-y-8">
            {steps.map((s) => (
              <div key={s.step} className="flex gap-6 items-start">
                <span className="text-3xl font-medium text-accent font-[Cormorant_Garamond] shrink-0 w-12">{s.step}</span>
                <div>
                  <h3 className="text-body font-medium mb-1">{s.title}</h3>
                  <p className="text-caption leading-relaxed">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <LocaleLink to="/registrieren">
              <Button variant="commit" size="lg" className="group">
                {t("handwerker.startNow")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* AI Search: Was ist CRAFTFINDUS? */}
      <WhatIsCraftfindus />

      {/* FAQ */}
      <section className="section-padding py-20 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <span className="text-micro tracking-[0.2em]">{t("handwerker.faqLabel")}</span>
          <h2 className="text-headline mt-4 mb-10">{t("handwerker.faqTitle")}</h2>
          <div>
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding py-20 border-t border-border text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-headline">{t("handwerker.ctaTitle")}</h2>
          <p className="text-caption leading-relaxed">{t("handwerker.ctaDesc")}</p>
          <LocaleLink to="/registrieren">
            <Button variant="commit" size="xl" className="group mt-4">
              {t("handwerker.ctaBtn")}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </LocaleLink>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FuerHandwerker;
